import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";

import { Path } from "~/v2/path";
import type { ApplicationDetailsProps } from "./ApplicationDetails.pres";
import type { GraphicsFieldsProps } from "./GraphicsFields/GraphicsFields.connector";
import { useSubmitAppClientLoad } from "./hooks/useSubmitAppClientLoad";
import { useSubmitAppRedirects } from "./hooks/useSubmitAppRedirects";
import { useSubmitAppSubmission } from "./hooks/useSubmitAppSubmission";
import type { PublishFooterProps } from "./PublishFooter.pres";
import type { SubmitAppPageProps } from "./SubmitApp.pres";
import {
  SUBMIT_APP_DEFAULT_VALUES,
  type SubmitAppFormValues,
  ZSubmitAppForm,
} from "./SubmitApp.schema";
import type { SubmitAppFieldErrors } from "./SubmitApp.types";
import type { SubmitAppModalProps } from "./SubmitAppModal.pres";

interface SubmitAppRouteParams {
  clientId?: string;
}

export const useSubmitAppController = () => {
  const { clientId } = useParams<SubmitAppRouteParams>();
  const { push } = useHistory();
  const {
    control,
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SubmitAppFormValues>({
    resolver: zodResolver(ZSubmitAppForm),
    defaultValues: SUBMIT_APP_DEFAULT_VALUES,
  });
  const values = watch();

  const clientLoad = useSubmitAppClientLoad({ clientId, reset });

  const navigateToClient = (id: string) => {
    push(Path.format(Path.ROUTE_ALPACA_CONNECT_APP, { clientId: id }));
  };

  const submission = useSubmitAppSubmission({
    clientId,
    handleSubmit,
    mode: clientLoad.mode,
    navigateToClient,
  });

  const redirectFields = useSubmitAppRedirects({
    control,
    getValues,
    redirectErrors: errors.redirect_uri,
    register,
    setValue,
  });

  const fieldErrors: SubmitAppFieldErrors = {
    name: errors.name?.message,
    categories: errors.categories?.message,
    description: errors.description?.message,
    redirectUris: errors.redirect_uri?.root?.message,
    url: errors.url?.message,
    termsOfUse: errors.terms_of_use?.message,
    privacyPolicy: errors.privacy_policy?.message,
    screenshots: errors.screenshots?.message,
  };

  const { screenshots: screenshotError, ...applicationErrors } = fieldErrors;

  const pageProps: SubmitAppPageProps = {
    mode: clientLoad.mode,
    isLoading: clientLoad.isLoading || submission.isSubmitting,
    hasEditLoadError: clientLoad.hasEditLoadError,
    isRetryingEditLoad: clientLoad.isRetryingEditLoad,
    onSubmit: submission.onSubmit,
    onBack: () => {
      if (clientId) {
        navigateToClient(clientId);
        return;
      }
      push(Path.ROUTE_ALPACA_CONNECT);
    },
    onRetryEditLoad: clientLoad.onRetryEditLoad,
  };

  const applicationDetailsProps: ApplicationDetailsProps = {
    mode: clientLoad.mode,
    values: {
      name: values.name,
      categories: values.categories,
      description: values.description,
      redirect_uri: values.redirect_uri,
      url: values.url,
      terms_of_use: values.terms_of_use,
      privacy_policy: values.privacy_policy,
    },
    fields: {
      name: register("name"),
      description: register("description"),
      url: register("url"),
      termsOfUse: register("terms_of_use"),
      privacyPolicy: register("privacy_policy"),
    },
    redirects: redirectFields.redirects,
    errors: applicationErrors,
    canAddRedirect: redirectFields.canAddRedirect,
    onCategoriesChange: (categories) => {
      setValue("categories", categories, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    onAddRedirect: redirectFields.onAddRedirect,
    onRemoveRedirect: redirectFields.onRemoveRedirect,
    onViewCredentials: submission.onViewCredentials,
  };

  const graphicsFieldsProps: GraphicsFieldsProps = {
    logo: values.logo,
    screenshots: values.screenshots,
    screenshotError,
    onLogoChange: (logo, options) => {
      setValue("logo", logo, options);
    },
    onScreenshotsChange: (screenshots, options) => {
      setValue("screenshots", screenshots, options);
    },
  };

  const publishFooterProps: PublishFooterProps = {
    mode: clientLoad.mode,
    status: values.status,
    didAgree: submission.didAgree,
    onToggleStatus: () => {
      setValue("status", values.status === "ACTIVE" ? "DISABLED" : "ACTIVE", {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    onAgreementChange: submission.onAgreementChange,
    onCancel: () => {
      push(Path.ROUTE_ALPACA_CONNECT);
    },
  };

  const modalProps: SubmitAppModalProps = {
    modal: submission.modal,
    isRegenerating: submission.isRegenerating,
    onClose: submission.onModalClose,
    onPrimary: submission.onModalPrimary,
  };

  return {
    pageProps,
    applicationDetailsProps,
    graphicsFieldsProps,
    publishFooterProps,
    modalProps,
  };
};
